// The contacts screen: the list, and one address in full.
//
// UI-9 put the numbers on top: a tab per country, each with its count, and
// over the list the chosen tab's figures — összes, feliratkozott,
// leiratkozott, soha nem iratkozott fel, kizárva, küldhető. They come from one
// grouped count the server keeps for a few minutes, and say when it was read.
//
// UI-10 adds presence: whether a contact row's profile is still in its
// country's Klaviyo account. A row whose profile is gone is not sent to; the
// list marks it, the detail's per-country table names it, and the figures
// count it once the API does.
//
// Two things shape the rest. The list is a haystack — 423 843 Hungarian contacts
// today, millions per country by the plan — so it pages with a cursor and a
// "Továbbiak" button rather than page numbers: there is no last page to jump
// to, and a page number would shift under the reader while an import runs.
// The search waits for the typing to stop, because every keystroke is a query
// against a table that size.
//
// The detail is the rule 6 screen. It opens on the verdict — may we mail this
// address, and if not, which signal stopped it and on what day — and
// everything under that is the evidence: every exclusion the address ever had,
// live and released, everything the provider sent back, and the lists it is
// on, when it is on any.
//
// Slice 5.5 adds "Levelek": every message the contact was sent, with what
// became of it, and the per-message figures the segments filter by
// (`pages/contacts/ContactMessages.tsx`).

import { useCallback, useEffect, useRef, useState } from "react";
import { Link, Navigate, useParams, useSearchParams } from "react-router-dom";
import { routeId } from "@/lib/utils";
import { ApiError, fetchContact, fetchContactSummary, fetchContacts } from "@/lib/api";
import type {
  Contact,
  ContactDeliveryEvent,
  ContactDetail,
  ContactListMembership,
  ContactSuppression,
} from "@/lib/api";
import type { ColumnDef } from "@tanstack/react-table";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { NativeSelect } from "@/components/ui/native-select";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { DataTable, TablePager } from "@/components/emc/DataTable";
import { DetailList } from "@/components/emc/DetailList";
import { EnumText } from "@/components/emc/EnumText";
import { PageHeader } from "@/components/emc/PageHeader";
import { StatGrid, StatTile } from "@/components/emc/StatTile";
import { StatusPill } from "@/components/emc/StatusPill";
import { Toolbar } from "@/components/emc/Toolbar";
import { EmptyState, ErrorState, LoadingState } from "@/components/emc/states";
import { usePageCrumb } from "@/components/emc/shell/crumb";
import { Country } from "@/components/emc/Flag";
import { labelOf, labelOptions, statusLook } from "@/components/emc/status";
import { useLoad } from "@/lib/useLoad";
import { ContactCountries } from "@/pages/contacts/ContactCountries";
import { ContactFlowRuns } from "@/pages/contacts/ContactFlowRuns";
import { ContactKlaviyoEvents } from "@/pages/contacts/ContactKlaviyoEvents";
import { ContactMessages } from "@/pages/contacts/ContactMessages";
import {
  countryParam,
  countryTabs,
  countsFor,
  presenceOf,
  refreshedAt,
  summaryFigures,
} from "@/pages/contacts/model";

// The tab of every country. Radix wants a value for it, and "" is the list's
// own word for "no country filter"; the two meet in `tabValue`.
const ALL_TAB = "osszes";

// The chosen tab lives in the address bar, so going back from an address's
// page lands on the same country.
const COUNTRY_PARAM = "orszag";

function tabValue(country: string): string {
  return country === "" ? ALL_TAB : country;
}

const PAGE_SIZE = 50;

// Long enough that a typed address is one query instead of twenty, short
// enough that the list still feels like it is answering.
const SEARCH_DELAY_MS = 350;

function errorMessage(error: unknown): string {
  if (error instanceof ApiError) return error.message;
  if (error instanceof Error) return error.message;
  return "Ismeretlen hiba.";
}

function when(value: string | null): string {
  return value === null ? "—" : new Date(value).toLocaleString("hu-HU");
}

function euro(cents: number): string {
  return `${(cents / 100).toLocaleString("hu-HU", { maximumFractionDigits: 2 })} €`;
}

function consentLabel(value: string): string {
  return labelOf("consent", value);
}

function fullName(contact: Contact): string {
  const name = `${contact.first_name} ${contact.last_name}`.trim();
  return name === "" ? "—" : name;
}

// The list's columns. The address opens the rule-6 page; the country and
// the times are data and set in the mono face; "kizárva" is a state.
const LIST_COLUMNS: ColumnDef<Contact>[] = [
  {
    id: "email",
    header: "Email",
    cell: ({ row }) => (
      <Link className="font-medium" to={`/kontaktok/${row.original.id}`}>
        {row.original.email}
      </Link>
    ),
  },
  { id: "country", header: "Ország", cell: ({ row }) => <Country code={row.original.country} /> },
  { id: "name", header: "Név", cell: ({ row }) => fullName(row.original) },
  { id: "city", header: "Város", cell: ({ row }) => (row.original.city === "" ? "—" : row.original.city) },
  {
    id: "consent",
    header: "Hozzájárulás",
    cell: ({ row }) => consentLabel(row.original.consent_status),
  },
  {
    id: "suppressed",
    header: "Kizárva",
    cell: ({ row }) =>
      row.original.is_suppressed ? (
        <StatusPill tone="danger">kizárva</StatusPill>
      ) : (
        <span className="text-muted-foreground">—</span>
      ),
  },
  {
    // UI-10: is the profile still in this country's Klaviyo account? Gone is
    // the state worth a pill — such a row is not sent to; present, the
    // ordinary case, is said in plain grey.
    id: "presence",
    header: "Klaviyo",
    cell: ({ row }) =>
      presenceOf(row.original.present) === "absent" ? (
        <StatusPill domain="presence" state="absent" />
      ) : (
        <span className="text-muted-foreground">{statusLook("presence", "present").label}</span>
      ),
  },
  {
    id: "lastOpen",
    header: "Utolsó megnyitás",
    cell: ({ row }) => when(row.original.last_open_at),
    meta: { mono: true, muted: true },
  },
  {
    id: "lastClick",
    header: "Utolsó kattintás",
    cell: ({ row }) => when(row.original.last_click_at),
    meta: { mono: true, muted: true },
  },
  {
    id: "source",
    header: "Forrás",
    cell: ({ row }) => (row.original.source === "" ? "—" : row.original.source),
    meta: { muted: true },
  },
];

export default function Contacts() {
  const [params, setParams] = useSearchParams();
  const country = countryParam(params.get(COUNTRY_PARAM));
  const [search, setSearch] = useState("");
  const [consent, setConsent] = useState("");
  const [suppressed, setSuppressed] = useState("");

  const [rows, setRows] = useState<Contact[]>([]);
  const [cursor, setCursor] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Which request the answers belong to. Two searches in flight can come back
  // in the other order, and the older one must not overwrite the newer list.
  const generation = useRef(0);

  const load = useCallback(
    (from: number | null) => {
      const mine = from === null ? ++generation.current : generation.current;
      setLoading(true);
      fetchContacts({
        search,
        country,
        consent,
        suppressed: suppressed === "" ? null : suppressed === "igen",
        cursor: from,
        limit: PAGE_SIZE,
      })
        .then((body) => {
          if (mine !== generation.current) return;
          setRows((current) => (from === null ? body.items : [...current, ...body.items]));
          setCursor(body.next_cursor);
          setError(null);
        })
        .catch((problem: unknown) => {
          if (mine !== generation.current) return;
          setError(errorMessage(problem));
        })
        .finally(() => {
          if (mine === generation.current) setLoading(false);
        });
    },
    [search, country, consent, suppressed],
  );

  useEffect(() => {
    const timer = window.setTimeout(() => load(null), SEARCH_DELAY_MS);
    return () => window.clearTimeout(timer);
  }, [load]);

  // The tabs and the figures: one request for every country, not one per tab.
  const [summary] = useLoad(fetchContactSummary, []);
  const tabs = countryTabs(summary.data);
  const counts = countsFor(summary.data, country);
  const refreshed = refreshedAt(summary.data?.computed_at);

  function chooseCountry(value: string) {
    const next = new URLSearchParams(params);
    if (value === ALL_TAB) next.delete(COUNTRY_PARAM);
    else next.set(COUNTRY_PARAM, value);
    setParams(next, { replace: true });
  }

  return (
    <>
      <PageHeader
        title="Kontaktok"
        description={
          <>
            Egy sorra kattintva előjön a cím teljes története: miért kap, vagy miért nem kap
            levelet. A kereső a cím elejére illeszt; <code>*</code> jellel kezdve a cím
            bármely részére, de az már végigolvassa a listát.
          </>
        }
      />

      <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
        <Tabs value={tabValue(country)} onValueChange={chooseCountry} className="min-w-0 max-w-full">
          <TabsList aria-label="Ország szerint">
            {tabs.map((tab) => (
              <TabsTrigger key={tab.value} value={tabValue(tab.value)}>
                {tab.value === "" ? tab.label : <Country code={tab.value} />}
                {tab.count !== null && <span className="text-label text-muted-foreground">{tab.count}</span>}
              </TabsTrigger>
            ))}
            {/* A country from an old link that no tab names still needs a
                tab to be the chosen one. */}
            {country !== "" && !tabs.some((tab) => tab.value === country) && (
              <TabsTrigger value={country}>
                <Country code={country} />
              </TabsTrigger>
            )}
          </TabsList>
        </Tabs>
        {refreshed !== null && (
          <span className="text-label text-muted-foreground" title={summary.data?.computed_at}>
            {refreshed}
          </span>
        )}
      </div>

      {summary.error !== null && (
        <ErrorState title="Az összesítés nem töltődött be." message={summary.error} className="mb-4" />
      )}
      {summary.data === null && summary.error === null && (
        <LoadingState lines={0} label="Az összesítés készül…" className="mb-4" />
      )}
      {counts !== null && (
        <StatGrid variant="strip" className="mb-4">
          {summaryFigures(counts).map((figure) => (
            <StatTile
              key={figure.key}
              size="sm"
              label={figure.label}
              value={figure.value}
              comparison={figure.comparison}
            />
          ))}
        </StatGrid>
      )}

      <Toolbar className="mb-3">
        <Input
          aria-label="Keresés címre"
          className="w-72"
          placeholder="Cím eleje, vagy *részlet"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
        />
        <NativeSelect
          aria-label="Hozzájárulás"
          value={consent}
          onChange={(event) => setConsent(event.target.value)}
        >
          <option value="">Minden hozzájárulás</option>
          {labelOptions("consent").map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </NativeSelect>
        <NativeSelect
          aria-label="Kizárás"
          value={suppressed}
          onChange={(event) => setSuppressed(event.target.value)}
        >
          <option value="">Kizárva és nem kizárva</option>
          <option value="igen">Csak a kizártak</option>
          <option value="nem">Csak a nem kizártak</option>
        </NativeSelect>
      </Toolbar>

      {error !== null && <ErrorState message={error} className="mb-4" />}

      <DataTable
        label="Kontaktok"
        columns={LIST_COLUMNS}
        data={rows}
        getRowId={(row) => String(row.id)}
        loading={loading}
        empty={<EmptyState bare title="Nincs találat." description="Más szűrés, vagy a cím egy másik eleje?" />}
        footer={
          <TablePager
            summary={`${rows.length} sor${cursor === null && rows.length > 0 ? " · ennyi van" : ""}`}
          >
            <Button
              variant="outline"
              type="button"
              disabled={cursor === null || loading}
              onClick={() => load(cursor)}
            >
              Továbbiak
            </Button>
          </TablePager>
        }
      />
    </>
  );
}

// --- the detail page's tables ---------------------------------------------

const SUPPRESSION_COLUMNS: ColumnDef<ContactSuppression>[] = [
  {
    id: "scope",
    header: "Hatókör",
    cell: ({ row }) => (row.original.country === "" ? "globális" : row.original.country),
    meta: { mono: true },
  },
  {
    id: "reason",
    header: "Ok",
    cell: ({ row }) => <EnumText domain="suppressionReason" value={row.original.reason} />,
  },
  {
    id: "source",
    header: "Forrás",
    cell: ({ row }) => <EnumText domain="suppressionSource" value={row.original.source} />,
  },
  {
    id: "actor",
    header: "Ki",
    cell: ({ row }) => (row.original.actor === "" ? "—" : row.original.actor),
    meta: { muted: true },
  },
  {
    id: "first",
    header: "Első jel",
    cell: ({ row }) => when(row.original.first_signal_at),
    meta: { mono: true, muted: true },
  },
  {
    id: "signals",
    header: "Jelek",
    cell: ({ row }) => row.original.signal_count,
    meta: { align: "right" },
  },
  {
    id: "campaign",
    header: "Kampány",
    cell: ({ row }) => (row.original.campaign_id === null ? "—" : row.original.campaign_id),
    meta: { mono: true, muted: true },
  },
  {
    id: "state",
    header: "Állapot",
    cell: ({ row }) => <SuppressionState row={row.original} />,
    meta: { wrap: true },
  },
];

/** A hold's cause, from the row's detail (rule 1, 2026-10-16). */
function holdCause(row: ContactSuppression): string | null {
  const cause = row.detail.cause;
  return row.reason === "held" && typeof cause === "string" ? cause : null;
}

// Live, released by a person, or a hold — live until its end, then over by
// itself (rule 1, 2026-10-16): „tartás”, its cause and when it ends.
function SuppressionState({ row }: { row: ContactSuppression }) {
  if (row.released_at !== null) {
    return (
      <span className="flex flex-wrap items-center gap-2">
        <StatusPill domain="suppression" state="released" />
        <span className="text-muted-foreground">
          {when(row.released_at)} · {row.released_by ?? "—"}
          {row.release_note === null ? "" : ` · „${row.release_note}”`}
        </span>
      </span>
    );
  }
  if (row.reason === "held") {
    const cause = holdCause(row);
    return (
      <span className="flex flex-wrap items-center gap-2">
        <StatusPill domain="suppression" state={row.is_active ? "held" : "ended"} />
        <span className="text-muted-foreground">
          {cause === null ? "" : `${labelOf("holdCause", cause)} · `}
          {row.is_active ? "eddig: " : "lejárt: "}
          <span className="data-mono">{when(row.expires_at ?? null)}</span>
        </span>
      </span>
    );
  }
  return <StatusPill domain="suppression" state="active" />;
}

const DELIVERY_COLUMNS: ColumnDef<ContactDeliveryEvent>[] = [
  {
    id: "when",
    header: "Mikor",
    cell: ({ row }) => when(row.original.occurred_at),
    meta: { mono: true, muted: true },
  },
  { id: "event", header: "Esemény", cell: ({ row }) => row.original.event_type },
  {
    id: "type",
    header: "Típus",
    cell: ({ row }) => {
      const event = row.original;
      return (
        <>
          {event.bounce_type ?? event.complaint_feedback_type ?? event.delay_type ?? "—"}
          {event.bounce_subtype === null ? "" : ` · ${event.bounce_subtype}`}
        </>
      );
    },
    meta: { muted: true },
  },
  {
    id: "code",
    header: "Kód",
    cell: ({ row }) => row.original.smtp_status ?? "—",
    meta: { mono: true },
  },
  {
    id: "campaign",
    header: "Kampány",
    cell: ({ row }) => (row.original.campaign_id === null ? "—" : row.original.campaign_id),
    meta: { mono: true, muted: true },
  },
  {
    id: "detail",
    header: "Részlet",
    cell: ({ row }) => row.original.diagnostic ?? "—",
    meta: { muted: true, wrap: true },
  },
];

const LIST_MEMBERSHIP_COLUMNS: ColumnDef<ContactListMembership>[] = [
  { id: "name", header: "Lista", cell: ({ row }) => row.original.name },
  { id: "country", header: "Ország", cell: ({ row }) => <Country code={row.original.country} /> },
  {
    id: "joined",
    header: "Csatlakozott",
    cell: ({ row }) => when(row.original.joined_at),
    meta: { mono: true, muted: true },
  },
  {
    id: "source",
    header: "Forrás",
    cell: ({ row }) => (row.original.source === "" ? "—" : row.original.source),
    meta: { muted: true },
  },
];

// --- one address in full ----------------------------------------------------

// The exclusion the API's verdict names: the first live row in the contact's
// own scope (global or its country), in the API's own order, an exclusion for
// good before a hold — the same row `_verdict` words its sentence from.
function blockingExclusion(contact: ContactDetail): ContactSuppression | null {
  const live = contact.suppressions.filter(
    (row) => row.is_active && (row.country === "" || row.country === contact.country),
  );
  return live.find((row) => row.reason !== "held") ?? live[0] ?? null;
}

function Verdict({ contact }: { contact: ContactDetail }) {
  // Not sendable because of an exclusion — as opposed to consent, which is
  // this country's row's own business and says nothing about other countries.
  const excluded =
    !contact.sendable &&
    (contact.is_suppressed ||
      contact.sendable_reason.startsWith("kizárva") ||
      contact.sendable_reason.startsWith("tartás"));
  const blocking = excluded ? blockingExclusion(contact) : null;
  const held = blocking !== null && blocking.reason === "held";
  const cause = blocking === null ? null : holdCause(blocking);
  return (
    <Card
      className={
        contact.sendable
          ? "border-transparent bg-green-soft shadow-none"
          : "border-transparent bg-red-soft shadow-none"
      }
    >
      <CardHeader>
        <div>
          <StatusPill domain="sendable" state={contact.sendable ? "yes" : "no"} />
        </div>
        <CardTitle className="mt-2">
          {blocking !== null && held ? (
            <>
              tartás
              {cause === null ? "" : `: ${labelOf("holdCause", cause)}`}{" "}
              <span className="data-mono">(eddig: {when(blocking.expires_at ?? null)})</span>
            </>
          ) : blocking !== null ? (
            <>
              kizárva: <EnumText domain="suppressionReason" value={blocking.reason} />{" "}
              <span className="data-mono">({blocking.first_signal_at.slice(0, 10)})</span>
            </>
          ) : (
            contact.sendable_reason
          )}
        </CardTitle>
        {excluded && held && (
          <CardDescription>
            A jel nem a címről szólt, hanem az IP-ről vagy a szolgáltatóról: a cím a
            tartás végéig kimarad, utána újra kaphat levelet. Ha egy újrapróba — a tartás kezdete
            után küldött levél — újra ilyen jelet kap, véglegesen kikerül; két kampány ugyanazon az
            estén egy találat.
          </CardDescription>
        )}
        {excluded && !held && (
          <CardDescription>
            A címről szóló jel (panasz, nem létező postafiók) minden országban véglegesen kizár; a
            leiratkozás és a kézi kizárás csak abban az országban, ahol történt.
          </CardDescription>
        )}
      </CardHeader>
    </Card>
  );
}

export function ContactDetailPage() {
  const params = useParams();
  const id = routeId(params.id);
  const [contact, setContact] = useState<ContactDetail | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (id === null) return;
    fetchContact(id)
      .then((body) => {
        setContact(body);
        setError(null);
      })
      .catch((problem: unknown) => setError(errorMessage(problem)));
  }, [id]);

  // The top bar's last word: the address this page is open on.
  usePageCrumb(contact?.email ?? null);

  if (id === null) return <Navigate to="/kontaktok" replace />;
  if (error !== null) {
    return (
      <>
        <PageHeader title="Kontakt" back={{ to: "/kontaktok", label: "Kontaktok" }} />
        <ErrorState message={error} />
      </>
    );
  }
  if (contact === null) {
    return (
      <>
        <PageHeader title="Kontakt" back={{ to: "/kontaktok", label: "Kontaktok" }} />
        <LoadingState lines={6} />
      </>
    );
  }

  const attributes = Object.entries(contact.attributes);

  return (
    <>
      <PageHeader
        back={{ to: "/kontaktok", label: "Kontaktok" }}
        title={contact.email}
        description={
          <>
            {fullName(contact)} · {contact.country} · {consentLabel(contact.consent_status)}
            {contact.language === "" ? "" : ` · ${contact.language}`}
          </>
        }
      />

      <div className="flex flex-col gap-6">
        <Verdict contact={contact} />

        <ContactCountries contact={contact} />

        <StatGrid>
          <StatTile label="Megnyitás (90 nap)" value={contact.opens_90d} />
          <StatTile label="Kattintás (90 nap)" value={contact.clicks_90d} />
          <StatTile label="Levél (30 nap)" value={contact.emails_received_30d} />
          <StatTile label="Rendelés" value={contact.orders_count} />
          <StatTile label="Élettartam-érték" value={euro(contact.ltv_cents)} />
        </StatGrid>

        <Card>
          <CardHeader>
            <CardTitle>Az adatlap</CardTitle>
          </CardHeader>
          <CardContent>
            <DetailList
              columns={2}
              items={[
                {
                  label: "Hozzájárulás forrása",
                  value: `${contact.consent_source === "" ? "—" : contact.consent_source} · ${when(contact.consent_at)}`,
                },
                { label: "Város", value: contact.city === "" ? "—" : contact.city },
                {
                  label: "Forrás",
                  value: `${contact.source === "" ? "—" : contact.source}${
                    contact.external_id === null ? "" : ` · ${contact.external_id}`
                  }`,
                },
                {
                  label: "Időzóna",
                  value: contact.timezone === "" ? "—" : contact.timezone,
                  mono: true,
                },
                {
                  label: "Átállási hullám",
                  value: `${contact.migration_wave === null ? "—" : contact.migration_wave}${
                    contact.wave_synced_at === null
                      ? ""
                      : ` · szinkronizálva ${when(contact.wave_synced_at)}`
                  }`,
                },
                {
                  label: "Utolsó megnyitás · kattintás",
                  value: `${when(contact.last_open_at)} · ${when(contact.last_click_at)}`,
                  mono: true,
                  muted: true,
                },
                {
                  label: "Utolsó rendelés",
                  value: when(contact.last_order_at),
                  mono: true,
                  muted: true,
                },
                {
                  label: "Felvéve · módosítva",
                  value: `${when(contact.created_at)} · ${when(contact.updated_at)}`,
                  mono: true,
                  muted: true,
                },
              ]}
            />
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Kizárások</CardTitle>
            <CardDescription>
              Minden sor, ami valaha erre a címre vonatkozott — a feloldottak is. A feloldás a
              történet része, nem a törlése.
            </CardDescription>
          </CardHeader>
          <CardContent className="flex flex-col gap-4">
            {contact.suppressions.length === 0 ? (
              <EmptyState title="Nincs kizárás ezen a címen." />
            ) : (
              <DataTable
                label="Kizárások"
                variant="plain"
                className="-mx-5.5 border-t border-separator"
                columns={SUPPRESSION_COLUMNS}
                data={contact.suppressions}
                getRowId={(row) => String(row.id)}
              />
            )}

            {contact.suppression_events.length > 0 && (
              <div className="flex flex-col gap-1 rounded-control border border-line bg-surface-2 p-3">
                {/* The API gives back at most fifty events, the newest of them:
                    an address in a bounce loop collects thousands. Saying so
                    matters — a log that silently starts late reads like the whole
                    story, and rule 6 is answered from this screen. */}
                <p className="caps-label mb-1">
                  A napló, időrendben
                  {contact.suppression_events.length === 50 ? " (a legutóbbi 50 jel)" : ""}
                </p>
                {contact.suppression_events.map((event) => (
                  <p key={event.id} className="text-muted-foreground">
                    <span className="data-mono">{when(event.occurred_at)}</span> ·{" "}
                    <strong className="font-medium text-ink">{event.action}</strong> · {event.reason}{" "}
                    · {event.source} · {event.actor}
                    {typeof event.detail.note === "string" && event.detail.note !== ""
                      ? ` · „${event.detail.note}”`
                      : ""}
                  </p>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Keyed by the contact: another contact's page starts its own list,
            and an answer still on its way for this one lands nowhere. */}
        <ContactMessages key={contact.id} contactId={contact.id} />

        {/* What Klaviyo saw this contact do (7.2 M2): the flows' signals. */}
        <ContactKlaviyoEvents key={`klaviyo-${contact.id}`} contactId={contact.id} />

        {/* What the flow engine in shadow decided for this contact (7.2 M3). */}
        <ContactFlowRuns key={`flows-${contact.id}`} contactId={contact.id} />

        <Card>
          <CardHeader>
            <CardTitle>Kézbesítés</CardTitle>
            <CardDescription>A legutóbbi 50 visszajelzés, a legfrissebb elöl.</CardDescription>
          </CardHeader>
          <CardContent>
            {contact.delivery_events.length === 0 ? (
              <EmptyState title="Erről a címről még nem érkezett visszajelzés." />
            ) : (
              <DataTable
                label="Kézbesítési visszajelzések"
                variant="plain"
                className="-mx-5.5 border-t border-separator"
                columns={DELIVERY_COLUMNS}
                data={contact.delivery_events}
                getRowId={(row) => String(row.id)}
              />
            )}
          </CardContent>
        </Card>

        {/* Only when there is any (UI-10): a country's subscription is its
            contact rows now, not a list, so most addresses are on none — and
            an empty card would read as something missing. */}
        {contact.lists.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle>Listák</CardTitle>
            </CardHeader>
            <CardContent>
              <DataTable
                label="Listák"
                variant="plain"
                className="-mx-5.5 border-t border-separator"
                columns={LIST_MEMBERSHIP_COLUMNS}
                data={contact.lists}
                getRowId={(row) => String(row.list_id)}
              />
            </CardContent>
          </Card>
        )}

        {attributes.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle>Amit a Klaviyo hozott</CardTitle>
              <CardDescription>
                Minden mező, aminek nincs saját oszlopa. Ahogy az export adta.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <DetailList
                items={attributes.map(([key, value]) => ({
                  label: <span className="data-mono">{key}</span>,
                  value: typeof value === "string" ? value : JSON.stringify(value),
                }))}
              />
            </CardContent>
          </Card>
        )}
      </div>
    </>
  );
}
